<template>
  <div class="border rounded bg-white p-3">
    <div class="flex justify-between items-center">
      <h2 class="font-bold">CSV Exports</h2>

      <button class="text-sm text-blue-600 hover:underline" @click="isOpen = !isOpen">
        {{ isOpen ? "Hide" : "Show" }}
      </button>
    </div>

    <div v-if="isOpen" class="mt-3">
      <div v-if="loading" class="text-sm text-gray-500">
        Loading CSV exports...
      </div>

      <div v-else-if="exports.length === 0" class="text-sm text-gray-500">
        No CSV exports yet.
      </div>

      <table v-else class="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th class="border p-2 text-left">Survey</th>
            <th class="border p-2 text-left">Date Range</th>
            <th class="border p-2 text-left">Status</th>
            <th class="border p-2 text-left">Created</th>
            <th class="border p-2 text-left">Completed</th>
            <th class="border p-2 text-left">Action</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in exports" :key="item.uuid">
            <td class="border p-2">
              {{ getSurveyName(item.survey_uuid) }}
            </td>

            <td class="border p-2">
              {{ formatDate(item.start_date) }} → {{ formatDate(item.end_date) }}
            </td>

            <td class="border p-2">
              <span
                :class="{
                  'text-green-600': item.status === 'completed',
                  'text-yellow-600': item.status === 'running' || item.status === 'pending',
                  'text-red-600': item.status === 'failed',
                }"
              >
                {{ item.status }}
              </span>

              <div v-if="item.error_message" class="text-xs text-red-600 mt-1">
                {{ item.error_message }}
              </div>
            </td>

            <td class="border p-2">
              {{ formatDateTime(item.created_at) }}
            </td>

            <td class="border p-2">
              {{ formatDateTime(item.completed_at) }}
            </td>

            <td class="border p-2">
              <a
                v-if="item.status === 'completed'"
                :href="getDownloadUrl(item)"
                class="text-green-600 hover:underline"
              >
                Download
              </a>

              <span v-else-if="item.status === 'running' || item.status === 'pending'" class="text-yellow-600">
                Preparing...
              </span>

              <span v-else class="text-gray-500">
                Unavailable
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <button
        class="mt-3 px-3 py-1 border rounded text-sm"
        :disabled="loading"
        @click="fetchExports"
      >
        Refresh
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "SurveyCsvExportsPanel",

  props: {
    surveyList: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isOpen: true,
      loading: false,
      exports: [],
      pollingTimer: null,
    };
  },

  created() {
    this.fetchExports();
    this.startPolling();
  },

  beforeUnmount() {
    clearInterval(this.pollingTimer);
  },

  methods: {
    async fetchExports() {
      this.loading = true;

      try {
        const { data } = await this.$http.get("/survey-csv-exports", {
          params: {
            per_page: 10,
          },
        });

        this.exports = data.data || data.exports || [];
      } catch (e) {
        this.$notify({ type: "error", text: "Failed to load CSV exports" });
      } finally {
        this.loading = false;
      }
    },

    startPolling() {
      this.pollingTimer = setInterval(() => {
        const hasRunningExports = this.exports.some(item =>
          item.status === "pending" || item.status === "running"
        );

        if (hasRunningExports) {
          this.fetchExports();
        }
      }, 5000);
    },

    getSurveyName(uuid) {
      const survey = this.surveyList.find(s => s.uuid === uuid);
      return survey ? survey.name : uuid;
    },

    getDownloadUrl(item) {
        return `/api/survey-csv-exports/${item.uuid}/download`;
    },

    formatDate(value) {
      if (!value) return "All";
      return String(value).substring(0, 10);
    },

    formatDateTime(value) {
      if (!value) return "-";
      return String(value).replace("T", " ").substring(0, 19);
    },
  },
};
</script>